import { FC, useCallback, useEffect, useState } from "react";
import { Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import useTranslation

import Flag from "icons/Flag";
import Cursor from "icons/Cursor";

import { Button } from "components/common/Button";
import { Input } from "components/common/inputs/Input";
import { ToastMessage } from "components/common/ToastMessage";

import {
  StyledExperimentInstructionsHeader,
  StyledExperimentInstructions,
  StyledExperimentInstructionsSubheader,
  StyledExperimentInstructionsList,
  StyledExperimentInstructionsItem,
  StyledExperimentInstructionsContent,
  StyledExperimentInstructionsOrder,
  StyledExperimentInstructionsBorderedContainer,
  StyledExperimentInstructionsCursor,
  StyledExperimentInstructionsQuestionContainer,
  StyledExperimentInstructionsQuestionHeader,
  StyledExperimentInstructionsOptions,
  StyledExperimentInstructionsOption,
} from "styles/features/experiment";

interface ExperimentInstructionsProps {
  experimentName?: string;
  canStartSession?: boolean;
  tasterStation?: string | null;
  request?: boolean;
  error?: string | null;

  onTasterStationChange?: (value: string) => void;
  handleToastClose: () => void;
  onStartSession?: () => void;
}

const ExperimentInstructions: FC<ExperimentInstructionsProps> = ({
  experimentName,
  canStartSession,
  error,
  onStartSession,
  handleToastClose,
  tasterStation,
  request,
  onTasterStationChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(); // Initialize translation
  const [stationInputValue, setStationInputValue] = useState<string>(
    tasterStation ?? ""
  );

  useEffect(() => {
    setStationInputValue(tasterStation ?? "");
  }, [tasterStation]);

  const handleInputChange = useCallback((newVal: string) => {
    setStationInputValue(newVal);
  }, []);

  const handleInputBlur = useCallback(() => {
    if (stationInputValue !== tasterStation) {
      onTasterStationChange?.(stationInputValue);
    }
  }, [stationInputValue, tasterStation, onTasterStationChange]);

  return (
    <StyledExperimentInstructions>
      <StyledExperimentInstructionsHeader>
        <Stack>
          <Typography
            variant="h1"
            color={theme.palette.common.black}
            textAlign={theme.direction === "rtl" ? "end" : "start"}
            marginBottom="8px"
          >
            {experimentName}
          </Typography>
          <Typography
            variant="bodyL"
            color={theme.palette.common.black}
            maxWidth="350px"
            textAlign={theme.direction === "rtl" ? "end" : "start"}
            marginBottom={2}
          >
            {t("ready_to_start")}
          </Typography>
          <Input
            placeholder={t("station_input_placeholder")}
            label={t("station_input_label")}
            autoFocus
            white
            value={stationInputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={request}
          />
        </Stack>
        <Button
          size="md"
          maxWidth="350px"
          onClick={onStartSession}
          disabled={!canStartSession}
        >
          {t("start_session")}
        </Button>
      </StyledExperimentInstructionsHeader>
      <StyledExperimentInstructionsSubheader>
        <Typography variant="h2" color={theme.palette.common.black}>
          {t("learn_to_use")}
        </Typography>
      </StyledExperimentInstructionsSubheader>
      <StyledExperimentInstructionsList>
        <StyledExperimentInstructionsItem>
          <StyledExperimentInstructionsContent>
            <StyledExperimentInstructionsOrder>
              1
            </StyledExperimentInstructionsOrder>
            <Typography
              variant="bodyL"
              color={theme.palette.common.black}
              fontWeight={400}
              textAlign={theme.direction === "rtl" ? "end" : "start"}
            >
              {t("help_instruction")}
            </Typography>
          </StyledExperimentInstructionsContent>
          <StyledExperimentInstructionsBorderedContainer>
            <Button
              variant="grey"
              startIcon={<Flag />}
              size="md"
              disabled
              className={"blue-bg"}
            >
              {t("need_help")}
            </Button>
            <StyledExperimentInstructionsCursor className="button-cursor">
              <Cursor />
            </StyledExperimentInstructionsCursor>
          </StyledExperimentInstructionsBorderedContainer>
        </StyledExperimentInstructionsItem>
        <StyledExperimentInstructionsItem>
          <StyledExperimentInstructionsContent>
            <StyledExperimentInstructionsOrder>
              2
            </StyledExperimentInstructionsOrder>
            <Typography
              variant="bodyL"
              color={theme.palette.common.black}
              fontWeight={400}
              textAlign={theme.direction === "rtl" ? "end" : "start"}
            >
              {t("instructions_field")}
            </Typography>
          </StyledExperimentInstructionsContent>
          <StyledExperimentInstructionsBorderedContainer className="lg">
            <Input
              multiline
              rows={6}
              disabled
              label={t("instructions")}
              value={t("some_instructions")}
              withErrorField={false}
              className="instructions-field"
            />
          </StyledExperimentInstructionsBorderedContainer>
        </StyledExperimentInstructionsItem>
        <StyledExperimentInstructionsItem>
          <StyledExperimentInstructionsContent>
            <StyledExperimentInstructionsOrder>
              3
            </StyledExperimentInstructionsOrder>
            <Typography
              variant="bodyL"
              color={theme.palette.common.black}
              fontWeight={400}
              textAlign={theme.direction === "rtl" ? "end" : "start"}
            >
              {t("finalize_answer")}
            </Typography>
          </StyledExperimentInstructionsContent>
          <StyledExperimentInstructionsBorderedContainer className="lg">
            <Stack
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap="32px"
            >
              <StyledExperimentInstructionsQuestionContainer>
                <StyledExperimentInstructionsQuestionHeader>
                  <Typography
                    variant="bodyL"
                    color={theme.palette.common.black}
                    fontWeight={500}
                    textAlign={theme.direction === "rtl" ? "end" : "start"}
                    width="100%"
                  >
                    {t("question")} #1
                  </Typography>
                </StyledExperimentInstructionsQuestionHeader>
                <Typography
                  variant="bodyM"
                  color={theme.palette.customGrey.dark}
                  fontWeight={300}
                  textAlign={theme.direction === "rtl" ? "end" : "start"}
                >
                  {t("similar_spreading")}
                </Typography>
              </StyledExperimentInstructionsQuestionContainer>
              <StyledExperimentInstructionsOptions>
                <StyledExperimentInstructionsOption className="active lg">
                  {t("option_1")}
                </StyledExperimentInstructionsOption>
                <StyledExperimentInstructionsOption className="lg">
                  {t("option_2")}
                </StyledExperimentInstructionsOption>
                <StyledExperimentInstructionsOption>
                  {t("the_same")}
                </StyledExperimentInstructionsOption>
              </StyledExperimentInstructionsOptions>
              <Button size="md" disabled className="blue-bg">
                {t("next_step")}
              </Button>
            </Stack>
            <StyledExperimentInstructionsCursor className="test-cursor">
              <Cursor />
            </StyledExperimentInstructionsCursor>
          </StyledExperimentInstructionsBorderedContainer>
        </StyledExperimentInstructionsItem>
        <StyledExperimentInstructionsItem>
          <StyledExperimentInstructionsContent>
            <StyledExperimentInstructionsOrder>
              4
            </StyledExperimentInstructionsOrder>
            <Typography
              variant="bodyL"
              color={theme.palette.common.black}
              fontWeight={400}
              textAlign={theme.direction === "rtl" ? "end" : "start"}
            >
              {t("rest_instruction")}
            </Typography>
          </StyledExperimentInstructionsContent>
          <StyledExperimentInstructionsBorderedContainer>
            <Button
              variant="secondary"
              size="md"
              disabled
              className={"blue-bg"}
            >
              {t("start_session")}
            </Button>
            <StyledExperimentInstructionsCursor className="button-cursor">
              <Cursor />
            </StyledExperimentInstructionsCursor>
          </StyledExperimentInstructionsBorderedContainer>
        </StyledExperimentInstructionsItem>
      </StyledExperimentInstructionsList>
      <ToastMessage
        severity="error"
        open={!!error}
        message={error as string}
        onClose={handleToastClose}
      />
    </StyledExperimentInstructions>
  );
};

export default ExperimentInstructions;
